import React, { useCallback, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Paperclip, X, Loader2, Download, ImageIcon, File as FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const isImage = (ct) => ct?.startsWith("image/") || ct?.includes("heic") || ct?.includes("heif");
const humanSize = (b) => b < 1024 ? `${b} B` : b < 1e6 ? `${(b/1024).toFixed(1)} KB` : `${(b/1e6).toFixed(1)} MB`;

export default function AttachmentPanel({ issueId }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!issueId) return;
    const r = await api.get("/attachments", { params: { issue_id: issueId } });
    setItems(r.data);
  }, [issueId]);

  React.useEffect(() => { load(); }, [load]);

  const doUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/attachments/upload?issue_id=${issueId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} file${files.length>1?"s":""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    doUpload(Array.from(e.dataTransfer.files));
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); doUpload(files); }
  };

  React.useEffect(() => {
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  const del = async (id) => {
    if (!window.confirm("Delete attachment?")) return;
    await api.delete(`/attachments/${id}`);
    load();
  };

  const token = localStorage.getItem("ft_token");
  return (
    <div>
      <div
        onDragOver={(e)=>{ e.preventDefault(); setDrag(true); }}
        onDragLeave={()=>setDrag(false)}
        onDrop={onDrop}
        data-testid="attachment-dropzone"
        className={cn("border border-dashed rounded-md p-3 text-xs text-muted-foreground flex items-center gap-2 transition-colors cursor-pointer",
          drag ? "dashed-drop text-primary" : "border-border hover:border-primary/40")}
        onClick={()=>inputRef.current?.click()}
      >
        <Paperclip className="w-4 h-4" />
        {uploading ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>
          : <span>Drop files, paste screenshots, or click to browse</span>}
        <input type="file" ref={inputRef} multiple hidden onChange={(e)=>doUpload(Array.from(e.target.files || []))} data-testid="attachment-file-input" />
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {items.map(a => (
            <div key={a.id} className="border border-border rounded-md p-2 flex items-center gap-2 text-xs group" data-testid={`attachment-${a.id}`}>
              {isImage(a.content_type) ? (
                <img src={`${API}/attachments/${a.id}/download?auth=${token}`} alt={a.original_filename} className="w-10 h-10 object-cover rounded" />
              ) : (
                <div className="w-10 h-10 grid place-items-center rounded bg-secondary text-muted-foreground"><FileIcon className="w-5 h-5" /></div>
              )}
              <div className="flex-1 min-w-0">
                <a href={`${API}/attachments/${a.id}/download?auth=${token}`} target="_blank" rel="noreferrer" className="block truncate hover:text-primary">{a.original_filename}</a>
                <div className="text-muted-foreground text-[10px]">{humanSize(a.size)}</div>
              </div>
              <button onClick={()=>del(a.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`del-attachment-${a.id}`}><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
